// ── Console warning interceptor for chart diagnostics ────────────────────────
// recharts reports rendering problems (bad domains, NaN values) via
// console.warn/error. Cells surface those as an inline warning icon instead of
// making users open devtools. The console patch installs only while at least
// one subscriber is mounted — importing this module has no global side effect,
// so pages that merely render saved layouts don't permanently patch console.

type WarningHandler = (msg: string) => void;

const handlers = new Set<WarningHandler>();

let origConsoleWarn: typeof console.warn | null = null;
let origConsoleError: typeof console.error | null = null;

// The console patch sees every warning fired anywhere in the app while a
// canvas is mounted (React, router, unrelated feature code) — forwarding all
// of it would pin noise on chart badges. Only surface messages that look like
// chart-rendering problems. Attribution across charts is still approximate:
// a console message can't be tied to the specific chart that triggered it, so
// a chart-shaped warning shows on every mounted cell.
const CHART_MESSAGE_PATTERN = /recharts|axis|domain|chart|NaN|Infinity/i;
// recharts' "width(0) and height(0)" is a chart measured while hidden (a
// Report in a background tab, a collapsed panel): layout, not a data problem,
// and it would pin a warning on every cell until the config changes.
const HIDDEN_CHART_PATTERN = /width\(0\) and height\(0\)/;

// recharts warns while its components render, and a handler sets state, so
// handlers run after the current task: React forbids updating one component
// while it renders another.
function notify(args: unknown[]) {
  const msg = args.map(String).join(' ');
  if (!CHART_MESSAGE_PATTERN.test(msg) || HIDDEN_CHART_PATTERN.test(msg))
    return;
  queueMicrotask(() => handlers.forEach((h) => h(msg)));
}

function install() {
  if (origConsoleWarn !== null) return;
  origConsoleWarn = console.warn;
  origConsoleError = console.error;
  console.warn = (...args: unknown[]) => {
    origConsoleWarn?.apply(console, args);
    notify(args);
  };
  console.error = (...args: unknown[]) => {
    origConsoleError?.apply(console, args);
    notify(args);
  };
}

function uninstall() {
  if (origConsoleWarn === null || origConsoleError === null) return;
  console.warn = origConsoleWarn;
  console.error = origConsoleError;
  origConsoleWarn = null;
  origConsoleError = null;
}

/**
 * Subscribe to console warnings/errors while mounted. Returns an unsubscribe
 * function. The console patch is installed with the first subscriber and
 * removed with the last.
 */
export function subscribeChartWarnings(handler: WarningHandler): () => void {
  if (handlers.size === 0) install();
  handlers.add(handler);
  return () => {
    handlers.delete(handler);
    if (handlers.size === 0) uninstall();
  };
}
